import { isEmailShaped } from "@/lib/email";
import type { BrokerQuote } from "@/lib/submissions/brokerSubmissions";
import { planSentence } from "@/lib/submissions/quoteCards";
import {
  BILLING_TYPE_LABELS,
  BILLING_TYPES,
  BIND_NOTE_MAX,
  PAYMENT_PLAN_LABELS,
  PAYMENT_PLANS,
  planOf,
  type BillingType,
  type BindContact,
  type BindRequestBody,
  type PaymentMethod,
  type PaymentPlan,
} from "@/lib/submissions/quoteBinding";

// The bind view's form (BRK-S209): what it starts from (the elections the
// quote holds, from its select), what it checks before sending, and the body
// it sends. Values stay plain on the wire (D-10): ISO dates, a decimal
// string for money. PMS checks it all again and its refusal is what counts.

export interface BindContactForm {
  name: string;
  email: string;
  phone: string;
}

export interface BindForm {
  billingType: BillingType | "";
  paymentPlan: PaymentPlan | "";
  paymentMethod: PaymentMethod | "";
  premiumFinanced: boolean;
  financeCompany: string;
  agreementRef: string;
  /** Undefined while empty. */
  downPayment: number | undefined;
  installments: number | undefined;
  /** ISO `YYYY-MM-DD`, or "" to choose. */
  requestedEffectiveDate: string;
  triaElection: "accepted" | "rejected" | "";
  primaryContact: BindContactForm;
  accountingContact: BindContactForm;
  applicationSignedOn: string;
  note: string;
  /** The insured accepted the terms and asked for the bind. */
  attested: boolean;
}

const NO_CONTACT: BindContactForm = { name: "", email: "", phone: "" };

/** An election still on offer, else the one option when there's only one,
 *  else none: a stored choice PMS would refuse isn't kept (BRK-S510). */
function preselect<T extends string>(
  stored: T | null | undefined,
  offered: readonly T[],
): T | "" {
  if (stored && offered.includes(stored)) return stored;
  return offered.length === 1 ? offered[0] : "";
}

/** The form as the quote's elections leave it. A past effective date isn't
 *  offered: PMS refuses one, so the broker chooses. */
export function bindFormFrom(quote: BrokerQuote, today: string): BindForm {
  const billing = quote.billing;
  const finance = billing?.premiumFinance ?? null;
  const effective = quote.policyEffectiveDate ?? "";
  return {
    billingType: preselect(
      billing?.billingType,
      billingOptions(quote).map((o) => o.value),
    ),
    paymentPlan: preselect(
      billing?.paymentPlan,
      planOptions(quote).map((o) => o.value),
    ),
    paymentMethod: billing?.paymentMethod ?? "",
    premiumFinanced: billing?.premiumFinanced ?? false,
    financeCompany: finance?.company ?? "",
    agreementRef: finance?.agreementRef ?? "",
    downPayment:
      finance?.downPayment == null ? undefined : Number(finance.downPayment),
    installments: finance?.installments ?? undefined,
    requestedEffectiveDate: effective >= today ? effective : "",
    triaElection: "",
    primaryContact: NO_CONTACT,
    accountingContact: NO_CONTACT,
    applicationSignedOn: "",
    note: "",
    attested: false,
  };
}

/** The billing types to elect: exactly what the carrier takes (PMS-S933,
 *  BRK-S510), else both, as before PMS sent it. */
export function billingOptions(
  quote: BrokerQuote,
): { value: BillingType; label: string }[] {
  const offered = quote.offeredBillingTypes ?? BILLING_TYPES;
  return BILLING_TYPES.filter((value) => offered.includes(value)).map(
    (value) => ({ value, label: BILLING_TYPE_LABELS[value] }),
  );
}

/** The plans to elect. Where PMS says what the carrier takes (PMS-S933,
 *  BRK-S510), exactly those: a plan the quote carries in its sentence, one
 *  only the carrier's setup names by its label. Before that, the quote's own,
 *  or every plan when the carrier offered none. Never "premium financed":
 *  financing is the yes/no beside the billing type, and PMS refuses it as a
 *  plan (`premium_financed_is_an_election`). */
export function planOptions(
  quote: BrokerQuote,
): { value: PaymentPlan; label: string }[] {
  const electable = (plan: PaymentPlan) => plan !== "premium_financed";
  const offered = quote.offeredPlans;
  if (offered) {
    return offered.flatMap((value) => {
      if (!PAYMENT_PLANS.includes(value) || !electable(value)) return [];
      const own = quote.paymentPlans.find((p) => planOf(p) === value);
      if (!own) return [{ value, label: PAYMENT_PLAN_LABELS[value] }];
      const { label, text } = planSentence(own);
      return [{ value, label: `${label}: ${text}` }];
    });
  }
  if (quote.paymentPlans.length === 0) {
    return PAYMENT_PLANS.filter(electable).map((value) => ({
      value,
      label: PAYMENT_PLAN_LABELS[value],
    }));
  }
  return quote.paymentPlans.flatMap((plan) => {
    const value = planOf(plan);
    if (!value || !electable(value)) return [];
    const { label, text } = planSentence(plan);
    return [{ value, label: `${label}: ${text}` }];
  });
}

/** Why the form can't be sent yet, by field; empty when it can. */
export function bindFormErrors(
  form: BindForm,
  today: string,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!form.billingType)
    errors.billingType = "Choose how the premium is billed.";
  if (!form.paymentPlan) errors.paymentPlan = "Choose a payment plan.";
  if (form.premiumFinanced) {
    if (!form.financeCompany.trim())
      errors["premiumFinance.company"] = "Enter the finance company.";
    if (!form.agreementRef.trim())
      errors["premiumFinance.agreementRef"] =
        "Enter the finance agreement’s number.";
  }
  if (!form.requestedEffectiveDate)
    errors.requestedEffectiveDate = "Choose the effective date to bind from.";
  else if (form.requestedEffectiveDate < today)
    errors.requestedEffectiveDate = "The effective date can’t be in the past.";
  if (form.applicationSignedOn && form.applicationSignedOn > today)
    errors.applicationSignedOn =
      "The application can’t be signed in the future.";
  for (const who of ["primaryContact", "accountingContact"] as const) {
    const email = form[who].email.trim();
    if (email && !isEmailShaped(email))
      errors[`${who}.email`] = "Enter a valid email address.";
  }
  if (form.note.trim().length > BIND_NOTE_MAX)
    errors.note = `Keep the note to ${BIND_NOTE_MAX.toLocaleString("en-US")} characters or fewer.`;
  if (!form.attested)
    errors.attested = "Confirm the insured asked you to bind this coverage.";
  return errors;
}

/** A contact with only what was answered; undefined with nothing. */
function contactOf(contact: BindContactForm): BindContact | undefined {
  const out: BindContact = {};
  for (const key of ["name", "email", "phone"] as const) {
    const value = contact[key].trim();
    if (value) out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** The body for a form `bindFormErrors` passed. Finance terms go only with a
 *  financed premium; an unanswered optional is left out, never sent empty. */
export function bindBody(form: BindForm): BindRequestBody {
  const body: BindRequestBody = {
    paymentMethod: form.paymentMethod || null,
    premiumFinanced: form.premiumFinanced,
  };
  if (form.billingType) body.billingType = form.billingType;
  if (form.paymentPlan) body.paymentPlan = form.paymentPlan;
  if (form.premiumFinanced) {
    body.premiumFinance = {
      company: form.financeCompany.trim(),
      agreementRef: form.agreementRef.trim(),
      ...(form.downPayment === undefined
        ? {}
        : { downPayment: form.downPayment.toFixed(2) }),
      ...(form.installments === undefined
        ? {}
        : { installments: form.installments }),
    };
  }
  if (form.requestedEffectiveDate)
    body.requestedEffectiveDate = form.requestedEffectiveDate;
  if (form.triaElection) body.triaElection = form.triaElection;
  const primary = contactOf(form.primaryContact);
  if (primary) body.primaryContact = primary;
  const accounting = contactOf(form.accountingContact);
  if (accounting) body.accountingContact = accounting;
  if (form.applicationSignedOn)
    body.applicationSignedOn = form.applicationSignedOn;
  const note = form.note.trim();
  if (note) body.note = note;
  return body;
}
